import { Tooltip } from "antd";
import { WalletOutlined, ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import { fetchWallet } from "../../../services/customer360Service";
import {
  cardStyle, inr, dateTime, statusChip, StatTile, TileGrid, PanelHeader,
  PanelTable, PanelSkeleton, PanelError, usePanelData,
} from "./shared";

/**
 * Money in and out of the customer's wallet.
 *
 * Credits and debits share one ledger, so the sign carries the meaning: a green
 * +₹ is money they put in, a red −₹ is money the product took. Reading a column
 * of bare numbers and working out which is which is the thing this avoids.
 */
const WalletTab = ({ userId, active }) => {
  const p = usePanelData(fetchWallet, userId, active);

  if (p.loading && !p.data) return <PanelSkeleton tiles={3} />;
  if (p.error) return <PanelError error={p.error} onRetry={p.reload} />;

  const d = p.data || {};

  const columns = [
    {
      title: "Date", dataIndex: "transaction_date", width: 165,
      render: (v) => <span style={{ fontSize: 12.5, color: "#475569", whiteSpace: "nowrap" }}>{dateTime(v)}</span>,
    },
    {
      title: "Credit", dataIndex: "credit", width: 120, align: "right",
      render: (v) => (Number(v) > 0
        ? <span style={{ color: "#16a34a", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>+{inr(v)}</span>
        : <span style={{ color: "#cbd5e1" }}>—</span>),
    },
    {
      title: "Debit", dataIndex: "debit", width: 120, align: "right",
      render: (v) => (Number(v) > 0
        ? <span style={{ color: "#dc2626", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>−{inr(v)}</span>
        : <span style={{ color: "#cbd5e1" }}>—</span>),
    },
    {
      title: "Reason", key: "reason", width: 200,
      /* One column for both sides — a row is only ever one of them, and two
         half-empty columns say less than one full one. */
      render: (_, r) => {
        const why = r.credit_source || r.debit_for;
        return why
          ? <span style={{ fontSize: 12.5, color: "#334155", fontWeight: 500 }}>{why}</span>
          : <span style={{ color: "#cbd5e1" }}>—</span>;
      },
    },
    {
      title: "Comment", dataIndex: "comment", width: 240,
      render: (v) => (v
        ? <Tooltip title={v}><span style={{ fontSize: 12.5, color: "#64748b" }}>{v}</span></Tooltip>
        : <span style={{ color: "#cbd5e1" }}>—</span>),
    },
    {
      title: "Balance", dataIndex: "total_amount", width: 130, align: "right",
      render: (v) => <span style={{ fontWeight: 600, color: "#0f172a", fontVariantNumeric: "tabular-nums" }}>{inr(v)}</span>,
    },
    { title: "Status", dataIndex: "status", width: 110, render: (v) => statusChip(v) },
  ];

  return (
    <>
      <TileGrid>
        <StatTile
          icon={<WalletOutlined />} label="Current balance" value={inr(d.current_balance)}
          color="#6366f1" bg="#eef2ff"
        />
        <StatTile
          icon={<ArrowUpOutlined />} label="Total added" value={inr(d.total_added)}
          color="#16a34a" bg="#f0fdf4"
        />
        <StatTile
          icon={<ArrowDownOutlined />} label="Total used" value={inr(d.total_used)}
          color="#dc2626" bg="#fef2f2"
        />
      </TileGrid>

      <div style={cardStyle}>
        <PanelHeader
          icon={<WalletOutlined />}
          title="Wallet ledger"
          subtitle="Every credit and debit against this account"
        />
        <PanelTable
          columns={columns}
          rows={p.rows}
          loading={p.loading}
          page={p.page}
          total={p.total}
          perPage={p.perPage}
          onPage={p.goToPage}
          empty="No wallet activity on this account yet."
          scrollX={1080}
        />
      </div>
    </>
  );
};

export default WalletTab;
